/**
 * Push nativo (FCM Android / APNs iOS) — lado cliente. **No-op total en web.**
 *
 * Piezas:
 *  - `usePushNotifications()`: hook montado una vez dentro del Router (ver
 *    `App.tsx`, molde `useNativeShell`). Con user logueado en nativo registra
 *    los listeners del plugin y, si el permiso del OS YA está concedido,
 *    re-registra el token en cada boot (los tokens rotan). NO pide permiso.
 *  - `requestPushPermission()`: pide el permiso del OS con gesto del user (el
 *    toggle de Perfil) y registra el device.
 *  - `unregisterPushDevice()`: des-registra el token en el backend (se llama
 *    ANTES del signout, mientras la sesión todavía autentica).
 *
 * El token viaja a `POST /api/push/devices`; el backend lo upsertea (un device
 * = un token; si otro user se loguea en el mismo device, se reasigna). El
 * import del plugin es dinámico para que el bundle web no lo cargue.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNative, nativePlatform } from './platform.js';
import { apiFetch } from './apiClient.js';
import { track } from './analytics.js';
import { useAuth } from '../auth/AuthProvider.js';

export type PushPermissionState = 'granted' | 'denied' | 'prompt' | 'unavailable';

/**
 * Canal Android propio, de importancia ALTA: es lo que hace que el push se
 * muestre como cartel arriba (heads-up), también con la app abierta. Sin canal
 * propio FCM cae a su fallback "Miscellaneous", de importancia normal: sólo
 * el ícono en la barra. EN SYNC con `FCM_ANDROID_CHANNEL_ID` del backend
 * (apps/backend/src/lib/push/fcm.ts, que lo nombra en cada push) y con el
 * meta-data `default_notification_channel_id` del AndroidManifest. La
 * importancia de un canal sólo puede BAJAR después de creado: para subirla
 * hay que usar un id nuevo.
 */
export const PUSH_CHANNEL_ID = 'orborix_push';

/** Último token entregado por el plugin — para el DELETE pre-signout. */
let lastRegisteredToken: string | null = null;

/**
 * ⚠️ El plugin es un Proxy de Capacitor que intercepta TODO acceso a
 * propiedades — incluida `.then`. Si una función async lo devuelve "pelado",
 * la resolución de la Promise hace el thenable-check (`plugin.then`) y el
 * proxy lanza `"PushNotifications.then()" is not implemented on android`.
 * Por eso se devuelve ENVUELTO en un objeto.
 */
async function loadPlugin() {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  return { plugin: PushNotifications };
}

async function postDeviceToken(token: string): Promise<void> {
  lastRegisteredToken = token;
  try {
    const res = await apiFetch('/api/push/devices', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ platform: nativePlatform(), token }),
    });
    if (!res.ok) {
      console.warn('[push] registro de device token falló', res.status);
    }
  } catch (err) {
    console.warn('[push] registro de device token falló', err);
  }
}

/** Estado actual del permiso del OS (para pintar el toggle de Perfil). */
export async function checkPushPermission(): Promise<PushPermissionState> {
  if (!isNative) return 'unavailable';
  const { plugin } = await loadPlugin();
  const status = await plugin.checkPermissions();
  if (status.receive === 'granted') return 'granted';
  if (status.receive === 'denied') return 'denied';
  return 'prompt';
}

/**
 * Pide el permiso del OS (si hace falta) y registra el device. Llamar SÓLO
 * desde un gesto del user (toggle de Perfil): el prompt del sistema es
 * one-shot en iOS y Android 13+ — quemado sin contexto, no vuelve.
 */
export async function requestPushPermission(): Promise<PushPermissionState> {
  if (!isNative) return 'unavailable';
  const { plugin } = await loadPlugin();
  let status = await plugin.checkPermissions();
  // `prompted`: the OS dialog was shown now (vs. an answer the OS already had).
  const prompted = status.receive === 'prompt' || status.receive === 'prompt-with-rationale';
  if (prompted) {
    status = await plugin.requestPermissions();
  }
  if (status.receive !== 'granted') {
    track('push_permission_result', { result: 'denied', prompted });
    return 'denied';
  }
  track('push_permission_result', { result: 'granted', prompted });
  await plugin.register(); // el token llega por el listener 'registration'
  return 'granted';
}

/** Des-registra el token en el backend. Llamar ANTES de `signout()`. */
export async function unregisterPushDevice(): Promise<void> {
  if (!isNative || !lastRegisteredToken) return;
  try {
    await apiFetch('/api/push/devices', {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token: lastRegisteredToken }),
    });
    lastRegisteredToken = null;
  } catch (err) {
    console.warn('[push] des-registro de device token falló', err);
  }
}

/**
 * Componente puente para montar el hook DENTRO de `<AuthProvider>` (el hook
 * usa `useAuth`; llamarlo en el cuerpo de `App()` crashea el boot — App
 * renderiza al provider, no vive bajo él). Ver `App.tsx`.
 */
export function PushNotificationsBridge(): null {
  usePushNotifications();
  return null;
}

/**
 * Listeners del plugin + re-registro silencioso. Montar una sola vez dentro
 * del Router (usa `useNavigate`) Y dentro de `<AuthProvider>` (usa `useAuth`)
 * — vía `PushNotificationsBridge`. No-op en web y sin user.
 */
export function usePushNotifications(): void {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!isNative || !userId) return;

    // Mismo protocolo de cleanup que `useNativeShell`: `addListener` es async;
    // si el efecto se limpia antes de resolver, el handle se remueve al llegar.
    let cancelled = false;
    const handles: PluginListenerHandle[] = [];
    const track = (handle: PluginListenerHandle): void => {
      if (cancelled) void handle.remove();
      else handles.push(handle);
    };

    void (async () => {
      const { plugin } = await loadPlugin();
      if (cancelled) return;

      // Idempotente: si ya existe, Android sólo actualiza nombre/descripción.
      if (nativePlatform() === 'android') {
        await plugin
          .createChannel({
            id: PUSH_CHANNEL_ID,
            name: 'Recordatorios',
            description: 'Tu racha y avisos para retomar la práctica',
            importance: 4, // IMPORTANCE_HIGH → heads-up
            visibility: 1, // VISIBILITY_PUBLIC
            vibration: true,
          })
          .catch((err: unknown) => {
            // Sin canal el push igual llega (fallback de FCM), sólo sin cartel.
            console.warn('[push] no se pudo crear el canal de notificaciones', err);
          });
        if (cancelled) return;
      }

      void plugin
        .addListener('registration', ({ value }) => {
          void postDeviceToken(value);
        })
        .then(track);

      void plugin
        .addListener('registrationError', (err) => {
          console.warn('[push] registration error', err);
        })
        .then(track);

      // Tap en la notificación → deep-link in-app (`data.path`, ej. '/home').
      void plugin
        .addListener('pushNotificationActionPerformed', (action) => {
          const path: unknown = (action.notification.data as Record<string, unknown> | undefined)?.[
            'path'
          ];
          if (typeof path === 'string' && path.startsWith('/')) void navigate(path);
        })
        .then(track);

      // Re-registro silencioso: sólo si el OS ya concedió el permiso (el
      // prompt es responsabilidad del toggle de Perfil, nunca del boot).
      const status = await plugin.checkPermissions();
      if (!cancelled && status.receive === 'granted') {
        await plugin.register();
      }
    })();

    return () => {
      cancelled = true;
      for (const handle of handles) void handle.remove();
    };
  }, [navigate, userId]);
}
